import * as React from 'react';
import {
	CustomWorkflowBundleType,
	isCustomWorkflowBundle,
	isFile,
	isFolder,
	isLink,
	isMedicalStudyBundle,
	isNote,
	isRequestListBundle,
	isSymbolicLink,
	isWorkflowBundle,
	Item,
} from '@citrite/sf-api';
import { fileIcons, IconProps } from '@sharefiledev/flow-web';
import { getIconTypeByExtension } from './iconTypes';

export interface ItemIconProps extends IconProps {
	item?: Item;
	fileName?: string;
}

export function ItemIcon(props: ItemIconProps) {
	const { item, fileName, size = 24, ...iconProps } = props;
	if (!item && !fileName) {
		throw new Error(`Need to specify an 'item' or a 'fileName'`);
	}
	const IconType = fileName ? getIconForExtension(fileName) : getIcon(item);
	return <IconType {...iconProps} size={size} />;
}

function getIcon(item: Item) {
	if (item.Id === 'box') {
		return fileIcons.FileboxIcon;
	} else if (isFolder(item) || isSymbolicLink(item)) {
		if (isCustomWorkflowBundle(item.Bundle)) {
			switch (item.Bundle.BundleSubType) {
				case CustomWorkflowBundleType.Form:
					return fileIcons.CwFormIcon;
				case CustomWorkflowBundleType.Template:
					return fileIcons.CwTemplateIcon;
				case CustomWorkflowBundleType.Workflow:
				default:
					return fileIcons.CwWorkflowIcon;
			}
		} else if (isMedicalStudyBundle(item.Bundle)) {
			return fileIcons.MedicalIcon;
		} else if (isRequestListBundle(item.Bundle)) {
			return fileIcons.RequestListIcon;
		} else if (isWorkflowBundle(item.Bundle)) {
			return fileIcons.ApprovalsFileIcon;
		}
		return fileIcons.FolderIcon;
	} else if (isFile(item)) {
		const name = item.FileName || item.Name;
		return getIconForExtension(name);
	} else if (isNote(item)) {
		return fileIcons.NoteIcon;
	} else if (isLink(item)) {
		return fileIcons.UrlIcon;
	}

	return fileIcons.FolderIcon;
}

function getIconForExtension(fileName: string) {
	const extension = fileName.substr(fileName.lastIndexOf('.') + 1).toLowerCase();
	switch (getIconTypeByExtension(extension)) {
		case 'illustrator':
			return fileIcons.IllustratorIcon;
		case 'air':
			return fileIcons.AirIcon;
		case 'word':
			return fileIcons.WordIcon;
		case 'encapsulatedPostScript':
			return fileIcons.EpsIcon;
		case 'flash':
			return fileIcons.FlashIcon;
		case 'indesign':
			return fileIcons.IndesignIcon;
		case 'audio':
			return fileIcons.AudioIcon;
		case 'noteworthyComposer':
			return fileIcons.NwcIcon;
		case 'navisworks':
			return fileIcons.NwfIcon;
		case 'pdf':
			return fileIcons.PdfIcon;
		case 'powerpoint':
			return fileIcons.PowerpointIcon;
		case 'photoshop':
			return fileIcons.PhotoshopIcon;
		case 'quickbooksBackup':
			return fileIcons.QbbIcon;
		case 'quickbooksWindows':
			return fileIcons.QbwIcon;
		case 'rivet':
			return fileIcons.RvtIcon;
		case 'video':
			return fileIcons.VideoIcon;
		case 'excel':
			return fileIcons.ExcelIcon;
		case 'zip':
			return fileIcons.ZipIcon;
		case 'code':
			return fileIcons.CodeFileIcon;
		case 'image':
			return fileIcons.ImagesIcon;
		case 'text':
			return fileIcons.TextIcon;
		case 'cad':
			return fileIcons.DwgIcon;
		case 'keynote':
			return fileIcons.KeynoteIcon;
		case 'diskCatalogMaker':
			return fileIcons.DcmIcon;
		case 'access':
			return fileIcons.MdbIcon;
		case 'project':
			return fileIcons.MppIcon;
		case 'numbers':
			return fileIcons.NumbersIcon;
		case 'pages':
			return fileIcons.PagesIcon;
		case 'publisher':
			return fileIcons.PubIcon;
		case 'visio':
			return fileIcons.VsdIcon;
		case 'sketch':
			return fileIcons.SketchIcon;
		case 'onenote':
			return fileIcons.OneIcon;
		case 'email':
			return fileIcons.EmailIcon;
		default:
			return fileIcons.UnknownIcon;
	}
}
